import { useEffect, useState } from "react";
import {
  Box,
  CardHeader,
  Dialog,
  Step,
  StepLabel,
  Stepper,
} from "../../../../../components";
import CarnePagamentoStepper from "./carne-pagamento-stepper.component";
import {
  ContasReceberService,
  MeioPagamentoService,
  NfSaidaService,
  notification,
  OperacaoFiscalService,
  PessoaService,
  SessionService,
} from "../../../../../services";
import { formatDate, responseErros } from "../../../../../utils";

const CarnePagamentoDialog = ({ open, setOpen, colunas }) => {
  const steps = ["Selecionar NF", "Selecionar Contas"];
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filtrosDialog, setFiltrosDialog] = useState({});
  const [listaPessoas, setListaPessoas] = useState([]);
  const [listNfSaida, setListNfSaida] = useState([]);
  const [listContasReceber, setListContasReceber] = useState([]);
  const [listaOperacoesFiscais, setListaOperacoesFiscais] = useState([]);
  const [nfSaidaSelecionado, setNfSaidasSelecionado] = useState([]);
  const [contasReceberSelecionadas, setContasReceberSelecionadas] = useState(
    []
  );
  const [listaMeiosPagamento, setListaMeiosPagamento] = useState([]);

  const nfSaidaService = new NfSaidaService();
  const contasReceberService = new ContasReceberService();
  const meioPagamentoService = new MeioPagamentoService();
  const pessoaService = new PessoaService();
  const operacaoFiscalService = new OperacaoFiscalService();
  const sessionService = new SessionService();
  const empresa = sessionService.getEmpresaRelatorio();

  function onClose() {
    setActiveStep(0);
    setListContasReceber([]);
    setListNfSaida([]);
    setNfSaidasSelecionado([]);
    setOpen(false);
  }

  useEffect(() => {
    if (open) {
      buscarMeiosPagamentos();
    }
  }, [open]);

  const buscarPessoas = (listaIds) => {
    const filtro = {
      id: listaIds.length > 0 ? listaIds : undefined,
      nonPaginated: true,
    };
    pessoaService.getAll(filtro).then((result) => {
      if (!result.isAxiosError) {
        const listaPessoas = result.data;
        setListaPessoas(listaPessoas);
      } else {
        responseErros(result);
      }
    });
  };

  const buscarNfSaida = async (filtro) => {
    setLoading(true);
    const porIntervalo = [];
    const meioPagamentoIds = listaMeiosPagamento.map((p) => p.id);
    const filtros = {
      ...filtro,
      status: [
        "PENDENTE",
        "CONTINGENCIA",
        "ENVIADA",
        "DENEGADA",
        "FATURADA",
        "EM PROCESSAMENTO",
      ],
      meioPagamentoId: meioPagamentoIds,
      restritiva: true,
    };
    if (filtros.dataInicial && filtros.dataFinal) {
      porIntervalo.push({
        coluna: "createdAt",
        de: filtros.dataInicial,
        ate: filtros.dataFinal,
      });
    }
    delete filtros.dataInicial;
    delete filtros.dataFinal;
    const result = await nfSaidaService.getAllFiltroAvancado({
      ...filtros,
      porIntervalo,
    });
    if (!result.isAxiosError) {
      setListNfSaida(result.data.rows);
      buscarRelacionamentosNfSaida(result.data.rows);
    } else {
      responseErros(result);
    }
    setLoading(false);
  };

  async function gerarCarne() {
    if (!contasReceberSelecionadas?.length)
      return notification.alertaGenericos(
        "Selecione ao menos uma conta para gerar"
      );
    setLoading(true);
    const filtros = {
      ids: contasReceberSelecionadas,
      empresa,
      clientes: listaPessoas,
    };
    const result = await contasReceberService.getCarnePagamentos(filtros);
    if (!result.isAxiosError) {
      openAbaDanfeBase64(result.data);
      onClose();
    } else {
      responseErros(result);
    }
    setLoading(false);
  }

  async function openAbaDanfeBase64(base64Pdf) {
    try {
      const response = await fetch(`data:application/pdf;base64,${base64Pdf}`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const pdfWindow = window.open(url, "_blank");
      if (!pdfWindow) {
        notification.alertaGenericos(
          "Popup bloqueado - por favor permita popups para este site"
        );
      }

      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      console.error("Erro ao abrir PDF:", error);
      throw error;
    }
  }

  const buscarContasReceber = async (filtro) => {
    setLoading(true);
    const filtros = {
      ...filtro,
      nfSaidaId: nfSaidaSelecionado[0],
      status: ["ABERTO", "BAIXADO_PARCIAL"],
      restritiva: true,
    };
    const result = await contasReceberService.getAllFiltroAvancado(filtros);
    if (!result.isAxiosError) {
      setListContasReceber(result.data.rows);
    } else {
      responseErros(result);
    }
    setLoading(false);
  };

  const buscarRelacionamentosNfSaida = (listaNfSaida) => {
    const idsPessoas = [];
    const idsOperacoesFiscais = [];
    for (const objeto of listaNfSaida) {
      if (objeto.pessoaClienteId) idsPessoas.push(objeto.pessoaClienteId);
      idsOperacoesFiscais.push(objeto.operacaoFiscalId);
    }
    buscarPessoas([...new Set(idsPessoas)]);
    buscarOperacoesFiscais([...new Set(idsOperacoesFiscais)]);
  };

  const buscarOperacoesFiscais = (listaIds) => {
    const filtro = {
      id: listaIds.length > 0 ? listaIds : undefined,
      nonPaginated: true,
    };
    operacaoFiscalService.getAll(filtro).then((result) => {
      if (!result.isAxiosError) {
        setListaOperacoesFiscais(result.data);
      } else {
        responseErros(result);
      }
    });
  };

  const buscarMeiosPagamentos = () => {
    const filtros = {
      ativado: true,
      nonPaginated: true,
      nfePagamento: "fpDuplicataMercantil",
    };
    meioPagamentoService.getAll(filtros).then((res) => {
      if (!res.isAxiosError) {
        setListaMeiosPagamento(res.data);
      } else {
        responseErros(res);
      }
    });
  };

  const handleDateChange = (date, fieldName) => {
    setFiltrosDialog((prevState) => ({
      ...prevState,
      [fieldName]: date != "Invalid Date" ? formatDate.toSend(date) : null,
    }));
  };

  const handleInputChange = (e) => {
    e.persist();
    setFiltrosDialog((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }));
  };

  const onChangeAutocomplete = (name, value) => {
    setFiltrosDialog({
      ...filtrosDialog,
      [name]: value ? value.id : undefined,
    });
  };

  return (
    <Dialog fullWidth maxWidth={"xl"} open={open} onClose={onClose}>
      <CardHeader title="Carnê de Pagamento" className="m-2" />
      <Box sx={{ width: "100%" }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>
      <CarnePagamentoStepper
        activeStep={activeStep}
        setActiveStep={setActiveStep}
        loading={loading}
        setLoading={setLoading}
        listNfSaida={listNfSaida}
        listContasReceber={listContasReceber}
        nfSaidaSelecionado={nfSaidaSelecionado}
        setNfSaidasSelecionado={setNfSaidasSelecionado}
        contasReceberSelecionadas={contasReceberSelecionadas}
        setContasReceberSelecionadas={setContasReceberSelecionadas}
        listaClientes={listaPessoas}
        listaOperacoesFiscais={listaOperacoesFiscais}
        listaMeiosPagamento={listaMeiosPagamento}
        buscarPessoas={buscarPessoas}
        handleInputChange={handleInputChange}
        handleDateChange={handleDateChange}
        onChangeAutocomplete={onChangeAutocomplete}
        buscarNfSaida={buscarNfSaida}
        buscarContasReceber={buscarContasReceber}
        gerarCarne={gerarCarne}
        filtrosDialog={filtrosDialog}
        colunas={colunas}
        onClose={onClose}
      />
    </Dialog>
  );
};

export default CarnePagamentoDialog;
